import { useCallback, useEffect, useMemo, useState } from 'react';
import * as bd from 'react-basic-design';
import {
    RedeemCodeDTO,
    useDeleteRedeemCodeApi,
    useSaveRedeemCodeApi,
    useSearchRedeemCodeApi,
} from '../../../api/redeem-code-api';
import SvgAdd from '../../../assets/icons/Add';
import { InitFailed } from '../../../components/basic/init-failed';
import { Loading } from '../../../components/basic/loading';
import { N } from '../../../components/basic/n';
import { notify } from '../../../components/basic/notify';
import { T, translate } from '../../../components/basic/text';
import { LoadingButton } from '../../../components/loading-button';
import { DateCell, ToggleCell } from '../../../components/table/table-cells';
import { PageNavbar } from '../../../components/ui/page-navbar';
import { TProvider } from '../../../i18n';
import { IBasicTableColumn } from '../../../interfaces/interfaces';
import { SaveRedeemCodeModal } from './save-redeem-code-modal';

export function RedeemCodeApp() {
    const [showModal, setShowModal] = useState(false);
    const [selectedRedeemCode, setSelectedRedeemCode] = useState<RedeemCodeDTO | undefined>();
    const redeemCodeInit = useSearchRedeemCodeApi();
    const saveRedeemCode = useSaveRedeemCodeApi();
    const deleteRedeemCode = useDeleteRedeemCodeApi();

    const { result, error, busy, initialized, initializing } = redeemCodeInit;

    const licenseTitle = useCallback(
        (serial?: number) => {
            var license = result?.licenses.find(x => x.serial === serial);
            return <span>{license ? `${license.licenseKey} ` : serial}</span>;
        },
        [result?.licenses]
    );

    const productTitle = useCallback(
        (id?: number) => {
            var product = result?.products.find(x => x.id === id);
            return <span>{product ? `${product.name} ` : id}</span>;
        },
        [result?.products]
    );

    const userTitle = useCallback(
        (userName?: string) => {
            var user = result?.users.find(x => x.userName === userName);
            return <span>{user ? `${user.firstName} ${user.lastName} ` : userName}</span>;
        },
        [result?.users]
    );

    const columns: IBasicTableColumn<RedeemCodeDTO>[] = useMemo(
        () => [
            {
                Header: <T>id</T>,
                name: 'id',
                width: 80,
                Cell: ({ row }) => <N>{row.id}</N>,
            },
            {
                Header: <T>code</T>,
                name: 'code',
                width: 80,
            },
            {
                Header: <T>product-id</T>,
                name: 'productId',
                width: 80,
                Cell: ({ row }) => productTitle(row.productId),
            },
            {
                Header: <T>used</T>,
                name: 'used',
                width: 80,
                Cell: ToggleCell,
            },
            {
                Header: <T>used-by</T>,
                name: 'usedBy',
                width: 80,
                Cell: ({ row }) => userTitle(row.usedBy),
            },
            {
                Header: <T>used-at</T>,
                name: 'usedAt',
                width: 80,
                Cell: DateCell,
            },
            {
                Header: <T>license-serial</T>,
                name: 'licenseSerial',
                width: 80,
                Cell: ({ row }) => licenseTitle(row.licenseSerial),
            },
        ],
        [licenseTitle, productTitle, userTitle]
    );

    useEffect(() => {
        if (initialized || initializing || error) return;
        redeemCodeInit.post(null, {});
    }, [initialized, initializing, error, redeemCodeInit]);

    const handleDelete = (redeemCode: RedeemCodeDTO) => {
        bd.msgbox(
            translate('delete-redeem-code'),
            translate('confirm-delete-redeem-code'),
            [
                {
                    title: translate('delete'),
                    variant: 'contained',
                    color: 'secondary',
                    action: hide => {
                        deleteRedeemCode.remove(
                            redeemCode,
                            () => {
                                redeemCodeInit.updateResult({
                                    ...result,
                                    redeemCodes: result.redeemCodes.filter(
                                        b => b.id !== redeemCode.id
                                    ),
                                });
                                notify.dark(translate('selected-redeem-code-deleted-successfully'));
                                hide();
                                setShowModal(false);
                                setSelectedRedeemCode(undefined);
                            },
                            notify.error
                        );
                    },
                },
                {
                    title: translate('cancel'),
                    variant: 'outline',
                    color: 'secondary',
                },
            ],
            { className: 'modal-n', backdropClassName: 'modal-n' }
        );
    };

    const handleSave = (redeemCode: RedeemCodeDTO) => {
        saveRedeemCode.post(
            null,
            redeemCode,
            res => {
                if (selectedRedeemCode) {
                    redeemCodeInit.updateResult({
                        ...result,
                        redeemCodes: result.redeemCodes.map(b =>
                            b.id === redeemCode.id ? res.redeemCode : b
                        ),
                    });
                } else {
                    redeemCodeInit.updateResult({
                        ...result,
                        redeemCodes: [res.redeemCode, ...result.redeemCodes],
                    });
                }

                setShowModal(false);
                setSelectedRedeemCode(undefined);
                notify.success(
                    selectedRedeemCode
                        ? translate('selected-redeem-code-updated-successfully')
                        : translate('new-redeem-code-created-successfully')
                );
            },
            notify.error
        );
    };

    if (error) return <InitFailed ex={error} onRetry={() => redeemCodeInit.post(null, {})} />;
    if (!initialized || initializing) return <Loading />;

    return (
        <TProvider>
            <div className="container">
                <PageNavbar
                    onGoBack="/home"
                    title="redeem-code"
                    controls={
                        <LoadingButton
                            variant="contained"
                            size="sm"
                            label={<T>new-redeem-code</T>}
                            icon={<SvgAdd />}
                            onClick={() => {
                                setShowModal(true);
                                setSelectedRedeemCode(undefined);
                            }}
                            loading={busy}
                        />
                    }
                />
                <bd.Paper className="h-100">
                    <bd.BasicTable
                        columns={columns}
                        data={result.redeemCodes}
                        onRowClick={({ row }) => {
                            setSelectedRedeemCode(row);
                            setShowModal(true);
                        }}
                        hover
                    />

                    <SaveRedeemCodeModal
                        show={showModal}
                        onHide={() => {
                            setShowModal(false);
                            setSelectedRedeemCode(undefined);
                        }}
                        onSave={handleSave}
                        onDelete={handleDelete}
                        selectedRedeemCode={selectedRedeemCode}
                        licenses={result?.licenses}
                        products={result?.products}
                        users={result?.users}
                        allowEdit={result.allowEdit}
                    />
                </bd.Paper>
            </div>
        </TProvider>
    );
}
